import { FC } from 'react';
import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { CloseButton, Drawer, Portal } from '@chakra-ui/react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import CustomButton from '../../../shared/CustomButton/CustomButton';
import CustomTextInput from '../../../shared/CustomInput/CustomTextInput';
import Typhography from '../../../shared/Typhography/Typhography';
import { ITrainingCategory } from '../../../models/allenamenti';
import { useCategoriesModal } from './useCategoriesModal';
import { SEARCH_THRESHOLD, countRows } from './categoriesModal.constants';
import CategoryGroup from './CategoryGroup';
import CategoryForm from './CategoryForm';
import styles from './CategoriesDrawer.module.scss';

interface ICategoriesDrawerProps {
  isOpen: boolean;
  categories: ITrainingCategory[];
  onClose: () => void;
}

/**
 * Gestione delle categorie di allenamento, in un pannello laterale.
 *
 * Non è una modal, ed è una scelta: qui convivono sedici categorie con le loro
 * sotto, ricerca, riordino, colori, creazione, modifica e spostamento. In una
 * finestra centrata non ci stanno — ogni dettaglio sistemato ne rompeva un
 * altro, e il form era finito come secondo dialogo sopra il primo, due riquadri
 * bianchi impilati. Il pannello dà altezza vera e lascia la settimana visibile
 * dietro, così si vede il contesto su cui si sta lavorando.
 *
 * Segue i drawer che il progetto ha già (`EditExerciseProgramDrawer`,
 * `EditBlockProgramDrawer`): stessa struttura Chakra, stesso comportamento.
 *
 * Le dodici macro di sistema non si rinominano né si eliminano — sono ciò che
 * rende confrontabile il lavoro fra società diverse e nel tempo (ADR 025) — ma
 * ci si possono creare dentro le proprie sottocategorie: è il caso normale,
 * dato che di sistema sono TUTTE le macro. Lo slug non compare mai, perché è la
 * chiave che i blocchi già scritti conservano e mostrarla inviterebbe a
 * cambiarla.
 */
const CategoriesDrawer: FC<ICategoriesDrawerProps> = ({
  isOpen,
  categories,
  onClose,
}) => {
  const m = useCategoriesModal(categories);
  const showSearch = countRows(categories) > SEARCH_THRESHOLD;

  /*
    Titolo del form: dice cosa si sta facendo e dentro cosa — è
    l'informazione che prima dava la posizione del form dentro l'elenco.
    `parentSlug` esiste solo fuori da 'idle', quindi il tipo va ristretto
    prima di leggerlo.
  */
  const parentSlug = m.mode.kind === 'idle' ? null : m.mode.parentSlug;
  const parentLabel = parentSlug
    ? categories.find((c) => c.slug === parentSlug)?.labelIt
    : null;
  const formTitle =
    m.mode.kind === 'edit'
      ? 'Modifica categoria'
      : parentLabel
        ? `Nuova voce in «${parentLabel}»`
        : 'Nuova categoria';

  const isForm = m.mode.kind !== 'idle';

  /*
    Soglia di 6px prima che il trascinamento parta: senza, un clic sulla
    maniglia verrebbe letto come drag e il riordino scatterebbe per sbaglio.
  */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const from = String(event.active.id);
    const to = event.over ? String(event.over.id) : null;
    if (!to || from === to) return;
    m.reorderTo(from, to);
  };

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(e) => {
        if (e.open) return;
        // Dal form si torna all'elenco; è dall'elenco che si chiude tutto.
        // Chiudere l'intera gestione per annullare una voce è sproporzionato.
        if (isForm) m.close();
        else onClose();
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content className={styles.panel}>
            <Drawer.CloseTrigger asChild zIndex={10}>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
            <Drawer.Header>
              <Typhography size="bold-20">
                {isForm ? formTitle : 'Categorie di allenamento'}
              </Typhography>
            </Drawer.Header>
            <Drawer.Body className={styles.scroll}>
              {isForm ? (
                <CategoryForm
                  draft={m.draft}
                  /* Una sottocategoria porta il colore della macro: mostrare la
             tavolozza darebbe un controllo senza effetto. */
                  showColor={parentSlug === null}
                  isValid={m.isValid}
                  isSaving={m.isSaving}
                  submitLabel={m.mode.kind === 'edit' ? 'Salva' : 'Aggiungi'}
                  error={m.error}
                  macros={m.mode.kind === 'edit' ? categories : undefined}
                  currentSlug={m.mode.kind === 'edit' ? m.mode.slug : undefined}
                  currentParent={parentSlug}
                  onMoveTo={
                    m.mode.kind === 'edit'
                      ? (parent: string | null) =>
                          m.moveTo((m.mode as { slug: string }).slug, parent)
                      : undefined
                  }
                  onChange={m.setField}
                  onSubmit={m.submit}
                  onClose={m.close}
                />
              ) : (
                <div className={styles.body}>
                  <div className={styles.toolbar}>
                    {/* Stesso campo di ricerca di UsersPage e SpacePage: input del
                progetto con la lente in coda, a larghezza naturale. */}
                    {showSearch ? (
                      <CustomTextInput
                        value={m.query}
                        placeholder="Cerca"
                        rightAddon={<MagnifyingGlassIcon />}
                        onChange={(e) => m.setQuery(e.target.value)}
                      />
                    ) : (
                      <span />
                    )}
                    <CustomButton
                      size="sm"
                      color="secondary"
                      icon="plus"
                      iconPosition="left"
                      onClick={() => m.startCreate(null)}
                    >
                      Nuova categoria
                    </CustomButton>
                  </div>

                  {m.error && (
                    <div className={styles.error} role="alert">
                      <Typhography
                        size="medium-12"
                        className={styles.errorText}
                      >
                        {m.error}
                      </Typhography>
                    </div>
                  )}

                  {/* `closestCenter` e non il default: `rectIntersection`
                      premia la zona con l'area sovrapposta maggiore, e un
                      gruppo aperto è molto più grande di una riga singola —
                      vincerebbe sempre lui e il rilascio su una riga non
                      verrebbe mai riconosciuto. */}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={m.visible.map((c) => c.slug)}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul className={styles.list}>
                        {m.visible.map((cat) => (
                          <CategoryGroup
                            key={cat.slug}
                            category={cat}
                            isOpen={m.isExpanded(cat.slug)}
                            onToggle={() => m.toggle(cat.slug)}
                            isSaving={m.isSaving}
                            pendingDelete={m.pendingDelete}
                            onStartCreate={m.startCreate}
                            onStartEdit={m.startEdit}
                            onAskDelete={m.askDelete}
                            onConfirmDelete={m.confirmDelete}
                          />
                        ))}
                      </ul>
                    </SortableContext>
                  </DndContext>

                  {m.visible.length === 0 && (
                    <Typhography size="regular-12" className={styles.empty}>
                      Nessuna categoria contiene «{m.query}».
                    </Typhography>
                  )}

                  <Typhography size="regular-10" className={styles.note}>
                    Le tue voci valgono per tutte le squadre. I nomi delle
                    categorie di sistema non si cambiano, ma puoi aggiungerci
                    dentro le tue.
                  </Typhography>
                </div>
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};

export default CategoriesDrawer;
